// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Importar Documentos (Admin)
// Escaneo de directorio + vista previa + importación batch
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import { AppHeader } from '../../../../shared/components/AppHeader';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import {
  escanearDirectorio,
  importarDocumentos,
  type ScanResult,
  type ImportResult,
  type ImportSummary,
} from '../../../../shared/services/api';

// ─── Types ───
type Step = 'config' | 'preview' | 'importing' | 'results';

// ─── Styles ───
const card: React.CSSProperties = {
  backgroundColor: C.surface,
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  overflow: 'hidden',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: '#fff',
  backgroundColor: C.azul2,
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s, opacity 0.15s',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: C.textSec,
  backgroundColor: 'transparent',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'ui-monospace, "Cascadia Code", monospace',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  backgroundColor: C.bg,
  color: C.text,
  outline: 'none',
};

const thStyle: React.CSSProperties = {
  padding: '14px 20px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: '#fff',
  backgroundColor: '#0E243E',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 20px',
  fontSize: 14,
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
};

// ─── Status badges ───
function ScanStatusBadge({ result }: { result: ScanResult }) {
  if (result.canImport) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 500,
          color: '#16A34A',
          backgroundColor: '#F0FDF4',
          border: '1px solid #16A34A22',
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16A34A' }} />
        Listo
      </span>
    );
  }
  if (result.existsInDb) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 500,
          color: '#F59E0B',
          backgroundColor: '#FFFBEB',
          border: '1px solid #F59E0B22',
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
        Ya existe
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color: '#EF4444',
        backgroundColor: '#FEF2F2',
        border: '1px solid #EF444422',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#EF4444' }} />
      No importable
    </span>
  );
}

function ImportStatusBadge({ status }: { status: ImportResult['status'] }) {
  const cfg = {
    created: { label: 'Creado', color: '#16A34A', bg: '#F0FDF4' },
    updated: { label: 'Actualizado', color: '#2563EB', bg: '#EFF6FF' },
    skipped: { label: 'Omitido', color: '#F59E0B', bg: '#FFFBEB' },
    error: { label: 'Error', color: '#EF4444', bg: '#FEF2F2' },
  }[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.color}22`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.color }} />
      {cfg.label}
    </span>
  );
}

// ─── Stepper ───
function Stepper({ current }: { current: Step }) {
  const steps: { key: Step; label: string; number: number }[] = [
    { key: 'config', label: 'Configurar origen', number: 1 },
    { key: 'preview', label: 'Vista previa', number: 2 },
    { key: 'importing', label: 'Importando', number: 3 },
    { key: 'results', label: 'Resultados', number: 4 },
  ];

  const currentIdx = steps.findIndex((s) => s.key === current);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
      {steps.map((step, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <div key={step.key} style={{ display: 'flex', alignItems: 'center' }}>
            {i > 0 && (
              <div
                style={{
                  width: 48,
                  height: 2,
                  backgroundColor: isDone ? C.azul2 : C.border,
                  transition: 'background 0.3s',
                }}
              />
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 14px',
                borderRadius: 20,
                backgroundColor: isActive ? `${C.azul2}12` : 'transparent',
                transition: 'background 0.3s',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  backgroundColor: isDone || isActive ? C.azul2 : C.border,
                  color: isDone || isActive ? '#fff' : C.textMut,
                  transition: 'all 0.3s',
                }}
              >
                {isDone ? <Ic d={ICON_PATHS.check} s={14} c="#fff" /> : step.number}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? C.text : C.textSec,
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export function ImportarPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // ─── State ───
  const [step, setStep] = useState<Step>('config');
  const [basePath, setBasePath] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [_importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  // ─── Handlers ───
  const handleScan = useCallback(async () => {
    if (!basePath.trim()) return;
    setScanning(true);
    setScanError(null);
    setScanResults([]);
    try {
      const res = await escanearDirectorio(basePath.trim());
      setScanResults(res.data);
      setStep('preview');
    } catch (err: any) {
      setScanError(err.message || 'Error al escanear el directorio');
    } finally {
      setScanning(false);
    }
  }, [basePath]);

  const handleImport = useCallback(async () => {
    setStep('importing');
    setImporting(true);
    setImportError(null);
    try {
      const res = await importarDocumentos(basePath.trim());
      setImportResults(res.data);
      setImportSummary(res.summary);
      setStep('results');
    } catch (err: any) {
      setImportError(err.message || 'Error al importar documentos');
      setStep('results');
    } finally {
      setImporting(false);
    }
  }, [basePath]);

  const handleReset = useCallback(() => {
    setStep('config');
    setBasePath('');
    setScanResults([]);
    setImportResults([]);
    setImportSummary(null);
    setScanError(null);
    setImportError(null);
    setExpandedErrors(new Set());
  }, []);

  const toggleError = useCallback((idx: number) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  // ─── Computed ───
  const importableCount = scanResults.filter((r) => r.canImport).length;
  const existingCount = scanResults.filter((r) => r.existsInDb).length;
  const errorCount = scanResults.filter((r) => !r.canImport && !r.existsInDb).length;

  return (
    <div
      style={{
        fontFamily: 'inherit',
      }}
    >
      <AppHeader modulo="Admin" titulo={t.importPage.titulo} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px' }}>
        {/* ═══ STEPPER ═══ */}
        <Stepper current={step} />

        {/* ═══ STEP 1: CONFIG ═══ */}
        {step === 'config' && (
          <div style={card}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
                Paso 1: Configurar origen
              </h2>
              <p style={{ fontSize: 13, color: C.textSec, margin: '6px 0 0' }}>
                Especifique la ruta al directorio que contiene los archivos DOCX a importar. El
                sistema escaneará recursivamente todos los subdirectorios.
              </p>
            </div>
            <div style={{ padding: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.text,
                  marginBottom: 8,
                }}
              >
                Ruta del directorio
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <input
                  type="text"
                  value={basePath}
                  onChange={(e) => setBasePath(e.target.value)}
                  placeholder={t.common.basePath}
                  style={inputStyle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleScan();
                  }}
                />
                <button
                  onClick={handleScan}
                  disabled={scanning || !basePath.trim()}
                  style={{
                    ...btnPrimary,
                    opacity: scanning || !basePath.trim() ? 0.5 : 1,
                    cursor: scanning || !basePath.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Ic d={ICON_PATHS.search} s={16} c="#fff" />
                  {scanning ? 'Escaneando...' : 'Escanear'}
                </button>
              </div>

              {scanError && (
                <div
                  style={{
                    marginTop: 16,
                    padding: '12px 16px',
                    borderRadius: 8,
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    color: '#DC2626',
                    fontSize: 13,
                  }}
                >
                  <strong>Error:</strong> {scanError}
                </div>
              )}

              <div
                style={{
                  marginTop: 20,
                  padding: '14px 18px',
                  borderRadius: 8,
                  backgroundColor: `${C.azul2}08`,
                  border: `1px solid ${C.azul2}18`,
                }}
              >
                <p style={{ fontSize: 12, color: C.textSec, margin: 0, lineHeight: 1.6 }}>
                  <strong>Formato esperado de archivo:</strong>{' '}
                  <code
                    style={{
                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                      backgroundColor: `${C.azul2}12`,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    XX.NN.TT.NN Nombre del Documento.docx
                  </code>
                  <br />
                  Ejemplo:{' '}
                  <code
                    style={{
                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                      backgroundColor: `${C.azul2}12`,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    PE.21.NC.01 Pirámide Documental.docx
                  </code>
                  <br />
                  Se aceptan también formatos con guiones bajos:{' '}
                  <code
                    style={{
                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                      backgroundColor: `${C.azul2}12`,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    PE_21_NC_01
                  </code>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: PREVIEW ═══ */}
        {step === 'preview' && (
          <div style={card}>
            <div
              style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
                  Paso 2: Vista previa
                </h2>
                <p style={{ fontSize: 13, color: C.textSec, margin: '6px 0 0' }}>
                  Se encontraron <strong>{scanResults.length}</strong> archivos DOCX en{' '}
                  <code
                    style={{
                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                      fontSize: 12,
                      backgroundColor: C.bg,
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    {basePath}
                  </code>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setStep('config')} style={btnSecondary}>
                  <Ic d={ICON_PATHS.arrowRight} s={14} c={C.textSec} />
                  Cambiar ruta
                </button>
                <button
                  onClick={handleImport}
                  disabled={importableCount === 0}
                  style={{
                    ...btnPrimary,
                    opacity: importableCount === 0 ? 0.5 : 1,
                    cursor: importableCount === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Ic d={ICON_PATHS.download} s={16} c="#fff" />
                  Importar {importableCount} documento{importableCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>

            {/* Summary cards */}
            <div
              style={{
                display: 'flex',
                gap: 16,
                padding: '16px 24px',
                borderBottom: `1px solid ${C.border}`,
                backgroundColor: C.bg,
              }}
            >
              <SummaryCard label="Total archivos" value={scanResults.length} color={C.azul2} />
              <SummaryCard label="Listos para importar" value={importableCount} color="#16A34A" />
              <SummaryCard label="Ya existentes" value={existingCount} color="#F59E0B" />
              <SummaryCard label="No importables" value={errorCount} color="#EF4444" />
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Archivo</th>
                    <th style={thStyle}>Código</th>
                    <th style={thStyle}>Tipo</th>
                    <th style={thStyle}>Proceso</th>
                    <th style={thStyle}>Título</th>
                    <th style={thStyle}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {scanResults.map((r, i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#F2F4F6' : C.surface }}>
                      <td
                        style={{
                          ...tdStyle,
                          fontSize: 12,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.filename}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 600,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                          color: r.code ? C.azul2 : C.textMut,
                        }}
                      >
                        {r.code ?? '—'}
                      </td>
                      <td style={tdStyle}>
                        {r.docType ? (
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: 4,
                              fontSize: 13,
                              fontWeight: 500,
                              backgroundColor: `${C.azul2}12`,
                              color: C.azul2,
                            }}
                          >
                            {r.docType}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={tdStyle}>
                        {r.processCode ? (
                          <span>
                            <span style={{ fontWeight: 600, color: C.azul2 }}>{r.processCode}</span>
                            {r.processName && (
                              <span style={{ color: C.textSec, marginLeft: 6, fontSize: 12 }}>
                                {r.processName}
                              </span>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.title}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <ScanStatusBadge result={r} />
                          {r.reason && (
                            <span style={{ fontSize: 11, color: C.textMut }}>{r.reason}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: IMPORTING ═══ */}
        {step === 'importing' && (
          <div style={card}>
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <div style={{ marginBottom: 20 }}>
                <LoadingSpinner size="md" muted />
              </div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>
                Importando documentos...
              </h2>
              <p style={{ fontSize: 14, color: C.textSec, margin: 0 }}>
                Procesando {importableCount} archivos DOCX. Esto puede tardar unos momentos.
              </p>
              {/* Progress bar */}
              <div
                style={{
                  width: 320,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: C.border,
                  margin: '24px auto 0',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    borderRadius: 2,
                    backgroundColor: C.azul2,
                    animation: 'progressIndeterminate 1.5s ease-in-out infinite',
                    width: '40%',
                  }}
                />
              </div>
              <style>{`
              @keyframes progressIndeterminate {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(350%); }
              }
            `}</style>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: RESULTS ═══ */}
        {step === 'results' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Error banner */}
            {importError && (
              <div
                style={{
                  padding: '16px 20px',
                  borderRadius: 10,
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  fontSize: 14,
                }}
              >
                <strong>Error durante la importación:</strong> {importError}
              </div>
            )}

            {/* Summary */}
            {importSummary && (
              <div style={card}>
                <div
                  style={{
                    padding: '20px 24px',
                    borderBottom: `1px solid ${C.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
                      Paso 4: Resultados de la importación
                    </h2>
                    <p style={{ fontSize: 13, color: C.textSec, margin: '6px 0 0' }}>
                      Proceso de importación finalizado
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={handleReset} style={btnSecondary}>
                      Nueva importación
                    </button>
                    <button onClick={() => navigate('/')} style={btnPrimary}>
                      <Ic d={ICON_PATHS.grid} s={16} c="#fff" />
                      Ir al explorador
                    </button>
                  </div>
                </div>

                {/* Summary cards */}
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    padding: '16px 24px',
                    borderBottom: `1px solid ${C.border}`,
                    backgroundColor: C.bg,
                  }}
                >
                  <SummaryCard
                    label="Total procesados"
                    value={importSummary.total}
                    color={C.azul2}
                  />
                  <SummaryCard label="Creados" value={importSummary.created} color="#16A34A" />
                  <SummaryCard label="Omitidos" value={importSummary.skipped} color="#F59E0B" />
                  <SummaryCard label="Errores" value={importSummary.errors} color="#EF4444" />
                </div>

                {/* Results table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Código</th>
                        <th style={thStyle}>Nombre</th>
                        <th style={thStyle}>Estado</th>
                        <th style={thStyle}>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.map((r, i) => (
                        <tr
                          key={i}
                          style={{ backgroundColor: i % 2 === 1 ? '#F2F4F6' : C.surface }}
                        >
                          <td
                            style={{
                              ...tdStyle,
                              fontWeight: 600,
                              fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                              color: C.azul2,
                            }}
                          >
                            {r.code}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {r.name}
                          </td>
                          <td style={tdStyle}>
                            <ImportStatusBadge status={r.status} />
                          </td>
                          <td style={tdStyle}>
                            {r.message ? (
                              <div>
                                <button
                                  onClick={() => toggleError(i)}
                                  style={{
                                    fontSize: 12,
                                    color: r.status === 'error' ? '#DC2626' : C.textSec,
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    textAlign: 'left',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <Ic
                                    d={
                                      expandedErrors.has(i)
                                        ? ICON_PATHS.chevDown
                                        : ICON_PATHS.chevRight
                                    }
                                    s={12}
                                    c={r.status === 'error' ? '#DC2626' : C.textSec}
                                  />
                                  {expandedErrors.has(i)
                                    ? r.message
                                    : r.message.length > 60
                                      ? r.message.slice(0, 60) + '...'
                                      : r.message}
                                </button>
                                {expandedErrors.has(i) && r.message.length > 60 && (
                                  <pre
                                    style={{
                                      fontSize: 11,
                                      color: C.textMut,
                                      margin: '6px 0 0',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-all',
                                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                                      backgroundColor: C.bg,
                                      padding: '8px 10px',
                                      borderRadius: 6,
                                    }}
                                  >
                                    {r.message}
                                  </pre>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: C.textMut }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Summary Card ───
function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '14px 18px',
        borderRadius: 8,
        backgroundColor: C.surface,
        border: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: `${color}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 800, color }}>{value}</span>
      </div>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.textSec }}>{label}</span>
    </div>
  );
}

export default ImportarPage;
